import { MessageSquare, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScoreOverrideDialog } from './score-override-dialog';
import { getScoreColor, scrollToTranscript } from './scoring-utils';
import type { QuestionScoreRow } from './types';

import { Badge } from '~/components/ui/badge';

interface AssessmentResultsProps {
  questions: QuestionScoreRow[];
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
}

export function AssessmentResults({
  questions,
  orgId,
  deptId,
  jobId,
  applicationId
}: AssessmentResultsProps): React.ReactNode {
  const { t } = useTranslation();
  const [editingQuestion, setEditingQuestion] = useState<QuestionScoreRow | null>(null);

  if (questions.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.assessment')}</h2>
      </div>
      <div className="flex flex-col gap-2">
        {questions.map((q) => (
          <div
            key={q.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm border-l-4 border-l-[var(--color-section-orange)]"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-1">
                {q.competencyName && (
                  <Badge variant="outline" className="w-fit text-xs">
                    {q.competencyName}
                  </Badge>
                )}
                <p className="text-sm font-medium">{q.questionText}</p>
              </div>
              {q.score !== null && (
                <button
                  type="button"
                  className="group/score shrink-0 flex items-center gap-1 cursor-pointer rounded px-1 -mx-1 hover:bg-muted/50"
                  onClick={() => {
                    setEditingQuestion(q);
                  }}
                >
                  <span className={`text-xl font-semibold ${getScoreColor(q.score)}`}>
                    {q.score % 1 === 0 ? q.score : q.score.toFixed(1)}
                  </span>
                  <span className="text-sm text-muted-foreground">/ 5</span>
                  <Pencil className="size-3 text-muted-foreground opacity-0 group-hover/score:opacity-100 transition-opacity" />
                </button>
              )}
            </div>
            <div className="mt-2 flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t('scoring.reasoning')}:</span> {q.reasoning}
              </p>
              <p className="text-sm italic text-muted-foreground">&ldquo;{q.evidence}&rdquo;</p>
            </div>
            <button
              type="button"
              onClick={() => {
                scrollToTranscript(q.questionId);
              }}
              className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <MessageSquare className="size-3" />
              {t('scoring.viewInTranscript')}
            </button>
          </div>
        ))}
      </div>

      {editingQuestion && editingQuestion.score !== null && (
        <ScoreOverrideDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditingQuestion(null);
          }}
          questionScoreId={editingQuestion.id}
          questionText={editingQuestion.questionText}
          currentValue={editingQuestion.score}
          mode="assessment"
          orgId={orgId}
          deptId={deptId}
          jobId={jobId}
          applicationId={applicationId}
        />
      )}
    </div>
  );
}
